/**
 * Clients live in `shared/` and not in `features/clients/`.
 *
 * Two screens need them: the client list, and the picker inside the quote editor. A
 * feature may import from `shared/` and never from another feature, so the moment the
 * quote editor needed a client the answer was to move the client, not to reach across.
 */
import { api, type Page } from '@/shared/api/client';

import type { Client, ClientCreate, ClientLookupRead, ClientUpdate } from './types';

export interface ClientListParams {
  search?: string;
  include_inactive?: boolean;
  limit?: number;
  offset?: number;
}

export const clientKeys = {
  all: ['clients'] as const,
  /** Every list, whatever its filters. Narrower than `all`, which is a prefix of the
   * detail key too: invalidating `all` would refetch a client that was just written. */
  lists: ['clients', 'list'] as const,
  list: (params: ClientListParams) => ['clients', 'list', params] as const,
  detail: (id: string) => ['clients', 'detail', id] as const,
};

export const clientsApi = {
  /**
   * The full list, searchable by name, ERP code and NIF at once.
   *
   * Full and not one lookup at a time, because the supplier's offer does not say which
   * customer it is for: Víctor picks the name out of everything Cubas has
   * (contexto §4.7).
   */
  list: (params: ClientListParams = {}): Promise<Page<Client>> =>
    api.get<Page<Client>>('/api/clients', { ...params }),

  detail: (id: string): Promise<Client> => api.get<Client>(`/api/clients/${id}`),

  create: (payload: ClientCreate): Promise<Client> => api.post<Client>('/api/clients', payload),

  /** Only the fields sent are written. Editing a client changes no quote already approved. */
  update: (id: string, payload: ClientUpdate): Promise<Client> =>
    api.patch<Client>(`/api/clients/${id}`, payload),

  /**
   * Ask Profer for one client by code or NIF and keep what it answers (contract v10.46):
   * the client added in Profer this morning, that the nightly list has not brought yet.
   * Only when a person asks: a name is not asked (`asked: false`), and Profer failing is an
   * answer with `error_code`, not an error.
   */
  lookup: (query: string): Promise<ClientLookupRead> =>
    api.post<ClientLookupRead>('/api/clients/lookup', { query }),
};
